<template>
  <div class="child-goals col-12">
    <div
      class="child-goals-wrapper"
      v-if="goals && goals.length > 0"
      :key="goalsContainerKey"
      ref="listContainer"
    >
      <template v-for="(goal, index) in goals">
        <goal-viewer
          class="child-goal draggable-item"
          :data-id="getGoalRelationshipId(goal, goalId)"
          :key="goal.id"
          :source="source"
          :goal-id="goal.id"
          :show-type="true"
          type="goal"
          :show-timeline="true"
          :show-gripper="true"
          :show-child-goals="true"
          :level="0"
          @archive-goal="refreshGoals"
          @delete-goal="refreshGoals"
          v-if="showAll || index < 9"
        ></goal-viewer>
      </template>
      <b-button
        v-if="goals.length > 9"
        variant="link"
        class="text-secondary"
        @click="showAll = !showAll"
        >{{ showAll ? 'Less' : 'More' }}</b-button
      >
    </div>
    <div class="text-secondary text-small pl-3" v-else-if="goals && goals.length === 0">
      No goals aligned to this goal
    </div>
    <div class="skeleton-loader" v-else>
      <goal-skeleton />
      <goal-skeleton />
      <goal-skeleton />
    </div>
  </div>
</template>

<script>
import { SOURCE } from '@/constants/analytics'
import events from '@/constants/events'
import EventBusMixin from '@/mixins/EventBusMixin'
import { mapActions } from 'vuex'
import { BButton } from 'bootstrap-vue'
import GoalViewer from '@/components/GoalViewer'
import GoalSkeleton from '@/components/skeletons/GoalSkeleton'
import DragAndDropMixin from '@/mixins/DragAndDropMixin'
import { trackEvent } from '../analytics'
import { EVENT_ACTION, EVENT_CATEGORY, EVENT_NAME } from '@/constants/analytics'
import showAPIError from '@/utils/showAPIError'

export default {
  name: 'ChildGoals',
  props: ['orgId', 'goalId'],
  mixins: [EventBusMixin, DragAndDropMixin],
  data() {
    return {
      goals: null,
      showAll: false,
      source: SOURCE.CHILD_GOALS_SECTION,
      goalsContainerKey: 1
    }
  },
  components: {
    BButton,
    GoalViewer,
    GoalSkeleton
  },
  methods: {
    ...mapActions(['getGoals', 'changeRank']),
    enableGoalDragAndDrop() {
      this.enableDragAndDrop({
        containers: [this.$refs.listContainer],
        onDrop: this.onDrop,
        handleName: 'goal-gripper',
        context: 'childGoals'
      })
    },
    async refreshGoals() {
      this.goals = await this.getGoals({
        orgId: this.orgId,
        options: {
          parentId: this.goalId,
          isArchived: false,
          expand: 'parent_goal_ids,parent_relationships',
          orderBy: 'rank_in_parent~id'
        }
      })
    },
    async onDrop({ afterId, beforeId, subjectId }) {
      try {
        await this.changeRank({
          orgId: this.orgId,
          type: 'child_goals',
          subjectId,
          beforeId,
          afterId
        })
        this.$emit('arrange', { subjectId, beforeId })
        trackEvent(EVENT_CATEGORY.ACTIVITY, EVENT_ACTION.REARRANGE, EVENT_NAME.CHILD_GOAL)
      } catch (e) {
        // Revert the drag. Re-rendering the list

        this.goalsContainerKey++
        this.$nextTick(function() {
          this.enableGoalDragAndDrop()
        })
        showAPIError(e)
      }
    },
    onChildGoalAddition({ parentIds }) {
      if (parentIds.includes(this.goalId)) {
        this.refreshGoals()
      }
    },
    getGoalRelationshipId(goal, parentId) {
      const parentRelationships = goal.parentRelationships
      const parentRelationship = parentRelationships?.find(x => x.parentId === parentId)
      return parentRelationship?.id
    }
  },
  async mounted() {
    await this.refreshGoals()
    this.enableGoalDragAndDrop()
    this.$nextTick(function() {
      this.enableGoalDragAndDrop()
    })
    this.listenToEventBus(events.CHILD_GOAL_CREATED, this.onChildGoalAddition)
  },
  watch: {
    goalId(goalId) {
      if (goalId) {
        this.refreshGoals()
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~@/styles/_styleConfigurations.scss';

.child-goals /deep/ .goal-title,
.child-goals /deep/ .goal-completion {
  font-size: 1rem;
}
.skeleton-loader {
  padding-left: 56px;
}
</style>
